/**
 * RN Tabbed Products Section JavaScript
 * Handles tab switching, slider navigation, and image gallery swipe
 */

(function() {
  'use strict';

  class RNTabbedProducts {
    constructor(section) {
      this.section = section;
      this.tabs = section.querySelectorAll('.rn-tprod__tab');
      this.panels = section.querySelectorAll('.rn-tprod__panel');

      this.init();
    }

    init() {
      this.bindTabs();
      this.initSliders();
      this.initGalleries();
    }

    bindTabs() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchTab(tab));
      });
    }

    switchTab(clickedTab) {
      const targetTab = clickedTab.dataset.tab;

      // Update tab states
      this.tabs.forEach(tab => {
        tab.classList.toggle('is-active', tab.dataset.tab === targetTab);
      });

      // Update panel states
      this.panels.forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === targetTab);
      });

      // Re-init slider for newly active panel
      const activePanel = this.section.querySelector(`.rn-tprod__panel.is-active`);
      if (activePanel) {
        this.initPanelSlider(activePanel);
      }
    }

    initSliders() {
      this.panels.forEach(panel => {
        this.initPanelSlider(panel);
      });
    }

    initPanelSlider(panel) {
      const slider = panel.querySelector('.rn-tprod__cards');
      if (!slider) return;

      const prevBtn = panel.querySelector('.rn-tprod__arrow--prev');
      const nextBtn = panel.querySelector('.rn-tprod__arrow--next');
      const cards = slider.querySelectorAll('.rn-tprod__card');

      if (cards.length === 0) return;

      // Calculate card width including gap
      const computedStyle = getComputedStyle(slider);
      const gap = parseFloat(computedStyle.gap) || 16;

      // Arrow navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          const cardWidth = cards[0].offsetWidth + gap;
          slider.scrollBy({ left: -cardWidth, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const cardWidth = cards[0].offsetWidth + gap;
          slider.scrollBy({ left: cardWidth, behavior: 'smooth' });
        });
      }

      // Touch swipe support for horizontal slider
      this.setupSwipe(slider);
    }

    setupSwipe(slider) {
      let startX = 0;
      let scrollLeft = 0;
      let isDown = false;

      slider.addEventListener('mousedown', (e) => {
        // Don't interfere with gallery swipes
        if (e.target.closest('[data-gallery-container]')) return;

        isDown = true;
        slider.classList.add('is-grabbing');
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });

      slider.addEventListener('mouseleave', () => {
        isDown = false;
        slider.classList.remove('is-grabbing');
      });

      slider.addEventListener('mouseup', () => {
        isDown = false;
        slider.classList.remove('is-grabbing');
      });

      slider.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        // Don't interfere with gallery swipes
        if (e.target.closest('[data-gallery-container]')) return;

        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeft - walk;
      });

      // Touch events - let native scroll handle it for smooth free-scrolling
      // No forced card-by-card snapping
    }

    /**
     * Initialize all image galleries on product cards
     */
    initGalleries() {
      const galleryCards = this.section.querySelectorAll('[data-rn-gallery]');

      galleryCards.forEach(card => {
        const gallery = card.querySelector('[data-gallery-container]');
        if (!gallery) return;

        const images = gallery.querySelectorAll('.rn-tprod__gallery-img');
        const dots = gallery.querySelectorAll('.rn-tprod__gallery-dot');

        if (images.length <= 1) return;

        let currentIndex = 0;
        let isTransitioning = false;

        // Function to show specific image
        const showImage = (index) => {
          if (isTransitioning || index === currentIndex) return;
          if (index < 0 || index >= images.length) return;

          isTransitioning = true;

          // Hide all images
          images.forEach((img) => {
            img.style.opacity = '0';
            img.classList.remove('is-active');
          });

          // Show target image
          if (images[index]) {
            images[index].style.opacity = '1';
            images[index].classList.add('is-active');
          }

          // Update dots
          dots.forEach((dot) => {
            dot.classList.remove('is-active');
          });

          if (dots[index]) {
            dots[index].classList.add('is-active');
          }

          currentIndex = index;

          // Reset transition flag after animation
          setTimeout(() => {
            isTransitioning = false;
          }, 300);
        };

        // Dot click handlers
        dots.forEach((dot) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const targetIndex = parseInt(dot.dataset.galleryDot, 10);
            showImage(targetIndex);
          });
        });

        // Touch/Swipe functionality
        let startX = 0;
        let startY = 0;
        let isSwiping = false;
        let swipeThreshold = 40;
        let velocityThreshold = 0.25;
        let startTime = 0;

        // Touch start
        gallery.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;

          const touch = e.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          startTime = Date.now();
          isSwiping = false;
          card.classList.remove('is-swiping');
        }, { passive: true });

        // Touch move
        gallery.addEventListener('touchmove', (e) => {
          if (e.touches.length !== 1) return;

          const touch = e.touches[0];
          const deltaX = touch.clientX - startX;
          const deltaY = touch.clientY - startY;

          // Determine if this is a horizontal swipe (image gallery)
          // vs vertical scroll (page scroll)
          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
            isSwiping = true;
            card.classList.add('is-swiping');
            e.preventDefault(); // Prevent page scroll during horizontal swipe
          }
        }, { passive: false });

        // Touch end
        gallery.addEventListener('touchend', (e) => {
          if (!isSwiping) {
            card.classList.remove('is-swiping');
            return;
          }

          const touch = e.changedTouches[0];
          const deltaX = touch.clientX - startX;
          const deltaTime = Date.now() - startTime;
          const velocity = Math.abs(deltaX) / deltaTime;

          // Check if swipe meets threshold requirements
          if (Math.abs(deltaX) > swipeThreshold || velocity > velocityThreshold) {
            if (deltaX > 0) {
              // Swipe right - go to previous image
              const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
              showImage(newIndex);
            } else {
              // Swipe left - go to next image
              const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
              showImage(newIndex);
            }
          }

          // Small delay before removing swiping class to prevent accidental link click
          setTimeout(() => {
            card.classList.remove('is-swiping');
            isSwiping = false;
          }, 50);
        }, { passive: true });

        // Mouse/Desktop drag support
        let isDragging = false;
        let mouseStartX = 0;

        gallery.addEventListener('mousedown', (e) => {
          mouseStartX = e.clientX;
          isDragging = false;
          e.preventDefault(); // Prevent text selection
        });

        gallery.addEventListener('mousemove', (e) => {
          if (e.buttons === 1) {
            const deltaX = e.clientX - mouseStartX;
            if (Math.abs(deltaX) > 10) {
              isDragging = true;
              card.classList.add('is-swiping');
            }
          }
        });

        gallery.addEventListener('mouseup', (e) => {
          if (isDragging) {
            const deltaX = e.clientX - mouseStartX;

            if (Math.abs(deltaX) > swipeThreshold) {
              if (deltaX > 0) {
                // Drag right - go to previous image
                const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
                showImage(newIndex);
              } else {
                // Drag left - go to next image
                const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
                showImage(newIndex);
              }
            }
          }

          setTimeout(() => {
            card.classList.remove('is-swiping');
            isDragging = false;
          }, 50);
        });

        gallery.addEventListener('mouseleave', () => {
          isDragging = false;
          card.classList.remove('is-swiping');
        });

        // Prevent context menu on long press
        gallery.addEventListener('contextmenu', (e) => {
          if (isDragging || isSwiping) {
            e.preventDefault();
          }
        });
      });
    }

    destroy() {
      // Cleanup if needed
    }
  }

  // Initialize all tabbed product sections
  function initTabbedSections() {
    const sections = document.querySelectorAll('.rn-tprod');
    sections.forEach(section => {
      new RNTabbedProducts(section);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabbedSections);
  } else {
    initTabbedSections();
  }

  // Re-init on Shopify section events (for theme editor)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.classList.contains('rn-tabbed-products-section')) {
      const section = event.target.querySelector('.rn-tprod');
      if (section) {
        new RNTabbedProducts(section);
      }
    }
  });

})();
